import { Box, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  getJobCandidateInteraction,
  ICandidate,
  IJob,
  IJobCandidateInteractionResponse,
  loadCandidateByCandidateId,
  getAllInteractions,
  getJobsFromAdvancedQuery,
  JobAssignmentStatusFilter,
  getJobByJobCode,
  addingContactLinkInCandidateMaster,
  getSubmissionSettings,
  deleteResume,
  fetchCandiateFieldMapping,
  candiateDefaultFieldMapping,
  getInformationRequestByInteractionId,
  updateRTRfeedback,
  getMyTeams,
} from 'wisestep-api';
import {
  IConversationState,
  ScrollableTabs,
  CandidateProfileTab,
  ActivityTab,
  SubmissionTimeline,
  ICandidateAggregatedData,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  IUserDTO,
  WsColors,
  getHoursOld,
} from 'wisestep-base';

import UpdateStatusModal from '../../../components/UpdateStatusModal/UpdateStatusModal';
import { useUserContext, useWorkspaceContext } from '../../../context';
import { updateMapping } from '../../../features/conversationWidget/utils/saveMapping';
import StageStatusContextProvider from '../../../features/submissions/context/StageStatusContext';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { syncPostgreAndESCandidate } from '../../candidates/utils/CandidateHelper';
import { useJobScreenContext } from '../context/JobScreenContext';
import IMappingFormState from '../types/IMappingFormState';
import IOtherDocument from '../types/IOtherDocument';
import {
  mappingScrollList,
  submissionScrollList,
  dncScrollList,
} from '../utils/conversationScrollList';
import { cvViewAndDownload } from '../utils/cvHelper';

import CandidateCallLogs from './CanddidateCallLogs';
import CandidateSmsLogs from './CandidateSmsLogs';
import ConversationWidgetLayout from './ConversationWidgetLayout';
import CustomDNCMessage from './CustomDNCMessage';
import CVViewModal from './CVViewModal';
import DataPrivacy from './DataPrivacy';
import EmailLogs from './EmailLogs';
import InteractionTaskList from './InteractionTaskList';
import JobSelection from './JobSelection';
import SubmissionHistory, { SwitchButton } from './SubmissionHistory';
import UpdateModal from './UpdateModal';

interface IConversationWidgetUI {
  state: IConversationState;
  selectedJob: IJob | null;
  aggregatedData: ICandidateAggregatedData | null;
}

const ConversationWidgetUI: FC<IConversationWidgetUI> = ({
  state,
  selectedJob,
  aggregatedData,
}) => {
  const [planIndex, setPlanIndex] = useState<number>(2);
  const [actionAfterJob, setActionAfterJob] = useState<string | null>(null);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const jobScreen = useJobScreenContext();
  const showJobScreen = jobScreen.showJobScreen;
  const [showJobSelectionScreen, setShowJobSelectionScreen] = useState<boolean>(false);
  const [openUpdateModal, setOpenUpdateModal] = useState<boolean>(false);
  const action = useConversationWidgetStore((state) => state.action);
  const [actionAndValue, setActionAndValue] = useState<{
    action: string;
    value: string | null;
  } | null>(null);
  const [activity, setActivity] = useState<IJobCandidateInteractionResponse[]>(
    aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []
  );
  const [activityHistory, setActivityHistory] = useState<IJobCandidateInteractionResponse | null>(
    null
  );
  const updateAction = useConversationWidgetStore((state) => state.updateAction);
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const candidate = state.candidate;
  const [profileInfo, setProfileInfo] = useState<ICandidate | null>(null);
  const [otherDocuments, setOtherDocuments] = useState<IOtherDocument[]>([]);
  const [isStateInitialized, setIsStateInitialized] = useState(false);
  const [totalConversation, setTotalConversation] = useState<string | null>(null);
  const [aggregation, setAggregation] = useState<ICandidateAggregatedData | undefined>(undefined);
  const view = useConversationWidgetStore((state) => state.view);
  const setTaskFilterJobId = useConversationWidgetStore((state) => state.setTaskFilterJobId);
  const taskFilterJobId = useConversationWidgetStore((state) => state.taskFilterJobId);
  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const refreshModal = useConversationWidgetStore((state) => state.updateId);
  const setSelectedPhoneNumber = useConversationWidgetStore(
    (state) => state.updateSelectedPhoneNumber
  );
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const updateActivityConversation = useConversationWidgetStore(
    (state) => state.updateActivityConversation
  );
  const isDnc = state.candidate.isInDnc ?? false;
  const [tabIndex, setTabIndex] = useState<number>(!isDnc ? 0 : view === 'submission' ? 0 : 1);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const closeUpdateStatusModal = () => setOpenStatusModal(false);
  const eraseData = useConversationWidgetStore((state) => state.eraseData);
  const [openCvView, setOpenCvView] = useState<boolean>(false);
  const [isJobAdminTeamMember, setIsJobAdminTeamMember] = useState<boolean>(false);
  const [resumeURl, setResumeURL] = useState<string | null>(null);
  const [updateStatusCandidateInteraction, setUpdateStatusCandidateInteraction] =
    useState<ICandidate>(state.candidate);

  const workspaceContext = useWorkspaceContext();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);

  const objectRTRExpired = {
    downloadSource: 'NOT_SPECIFIED',
    recruiterActivity: {
      actions: ['RTR Expired'],
      results: [],
    },
  };

  const [formState] = useState<IMappingFormState>({
    resumeURl: null,
    openCvView: false,
    candidateInfo: activityConversation ?? state.candidate,
    candidateResumes: [],
    disableSubmit: false,
    results: [],
    actions: [],
    jobForUpdate: null,
  });

  useEffect(() => {
    if (action === 'smslogs') {
      setTabIndex(3);
      setPlanIndex(1);
    }
  }, [action]);

  useEffect(() => {
    if (workspaceContext.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e') {
      (async () => {
        if (candidate.jobId) {
          const resInterationData = await getJobCandidateInteraction(state.candidate.id as string);
          const mappingTimeLineObject = JSON.stringify(
            resInterationData?.data?.contactActionsObjs || []
          );
          const hasRTRStatus = (status) => mappingTimeLineObject.includes(status);
          if (hasRTRStatus('RTR Requested')) {
            const interactionId = `${state.candidate.candidateId}_${state.candidate.jobId}`;
            if (!['RTR Expired', 'RTR Accepted', 'RTR Rejected'].some(hasRTRStatus)) {
              const rtrByInformationTemplate = await getInformationRequestByInteractionId(
                interactionId
              );
              const expiryHours = getHoursOld(rtrByInformationTemplate?.data[0]?.requestedOn);
              if (expiryHours > 72) {
                await updateRTRfeedback({
                  id: rtrByInformationTemplate.data[0].id,
                  rtrFeedback: 'RTR Expired',
                });

                formState.candidateInfo = state.candidate;
                if (formState.candidateInfo) {
                  await updateMapping(objectRTRExpired, formState.candidateInfo);
                }
              }
            }
          }
        }
      })();
    }
  }, [candidate, loggedInUser]);

  useEffect(() => {
    (async () => {
      if (candidate.jobId) {
        const teamBelongsto = await getMyTeams();
        const currentUserTeamIds = teamBelongsto.data?.map((team) => team.id);
        setIsJobAdminTeamMember(currentUserTeamIds.includes(candidate?.jobPostedByTeam?.id));
      }
    })();
  }, [candidate, loggedInUser]);

  useEffect(() => {
    if (
      actionAfterJob === 'email' ||
      actionAfterJob === 'sms' ||
      actionAfterJob === 'call' ||
      actionAfterJob === 'task'
    ) {
      updateAction(actionAfterJob);
    }
  }, [actionAfterJob, updateAction]);

  const redirectToJob = async (jobCode: string) => {
    const response = await getJobsFromAdvancedQuery({
      jobCodes: [jobCode],
      page: 0,
      size: 2,
      sort: ['scoringData.score,desc'],
      searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
      jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
      jobStatus: 'All',
    });

    if (!isEmpty(response.data) && response.data[0]) {
      openUrlInNewTab(`/jobs-crm/${response.data[0].id}/false/0/pipeline/`);
    }
    if (isEmpty(response.data)) {
      const jobResponse = await getJobByJobCode(jobCode);
      openUrlInNewTab(`/jobs-crm/${jobResponse.data.id}/false/0/pipeline/`);
    }
  };
  const [updateResume, setUpdateResume] = useState<boolean>(false);

  const setIndex = useCallback(
    async (index: number) => {
      if (index === 0 && view === 'submission' && aggregatedData?.candidateEntity.candidateId) {
        const res = await loadCandidateByCandidateId(aggregatedData.candidateEntity.candidateId);

        res.data.passportNumber = isEmpty(res.data.passportNumber)
          ? aggregatedData.candidateEntity.passportNumber
          : res.data.passportNumber;

        res.data.passportType = isEmpty(res.data.passportType)
          ? aggregatedData.candidateEntity.passportType
          : res.data.passportType;

        res.data.dob = isEmpty(res.data.dob) ? aggregatedData.candidateEntity.dob : res.data.dob;

        res.data.gender = isEmpty(res.data.gender)
          ? aggregatedData.candidateEntity.gender
          : res.data.gender;

        res.data.age = isEmpty(res.data.age) ? aggregatedData.candidateEntity.age : res.data.age;

        res.data.postalAddress = isEmpty(res.data.postalAddress)
          ? aggregatedData.candidateEntity.postalAddress
          : res.data.postalAddress;

        res.data.ssnNumber = isEmpty(res.data.ssnNumber)
          ? aggregatedData.candidateEntity.ssnNumber
          : res.data.ssnNumber;

        res.data.panCard = isEmpty(res.data.panCard)
          ? aggregatedData.candidateEntity.panCard
          : res.data.panCard;

        res.data.aadharNumber = isEmpty(res.data.aadharNumber)
          ? aggregatedData.candidateEntity.aadharNumber
          : res.data.aadharNumber;
        syncPostgreAndESCandidate(res.data, state.candidate);
        setProfileInfo(res.data);
        const interactionId = getInteractionId(res, candidate);
        const interactionRecord =
          interactionId && (await getJobCandidateInteraction(interactionId));
        interactionRecord && setOtherDocuments(interactionRecord.data.otherDocuments);
      } else if (index === 1 && aggregatedData?.candidateEntity?.candidateId) {
        const res = await loadCandidateByCandidateId(aggregatedData.candidateEntity.candidateId);

        res.data.passportNumber = isEmpty(res.data.passportNumber)
          ? aggregatedData.candidateEntity?.passportNumber
          : res.data.passportNumber;

        res.data.passportType = isEmpty(res.data.passportType)
          ? aggregatedData.candidateEntity?.passportType
          : res.data.passportType;

        res.data.dob = isEmpty(res.data.dob) ? aggregatedData.candidateEntity?.dob : res.data.dob;

        res.data.gender = isEmpty(res.data.gender)
          ? aggregatedData.candidateEntity.gender
          : res.data.gender;

        res.data.age = isEmpty(res.data.age) ? aggregatedData.candidateEntity?.age : res.data.age;

        res.data.postalAddress = isEmpty(res.data.postalAddress)
          ? aggregatedData.candidateEntity?.postalAddress
          : res.data.postalAddress;

        res.data.ssnNumber = isEmpty(res.data.ssnNumber)
          ? aggregatedData.candidateEntity?.ssnNumber
          : res.data.ssnNumber;

        res.data.panCard = isEmpty(res.data.panCard)
          ? aggregatedData.candidateEntity?.panCard
          : res.data.panCard;

        res.data.aadharNumber = isEmpty(res.data.aadharNumber)
          ? aggregatedData.candidateEntity?.aadharNumber
          : res.data.aadharNumber;
        syncPostgreAndESCandidate(res.data, state.candidate);
        setProfileInfo(res.data);
        setOtherDocuments(
          isEmpty(res.data.otherDocuments)
            ? aggregatedData.candidateEntity?.otherDocuments
            : res.data.otherDocuments
        );
      } else if (index === 2) {
        setTaskFilterJobId(null);
      }
      setTabIndex(index);
    },
    [aggregatedData?.candidateEntity, candidate, setTaskFilterJobId, view]
  );

  const getInteractionId = (res, candidate: ICandidate) => {
    let interactionId: string | null = null;
    if (res.data?.recentSubmission1?.jobCode === candidate.jobCode) {
      interactionId = res.data?.recentSubmission1?.id;
    } else if (res.data?.recentSubmission2?.jobCode === candidate.jobCode) {
      interactionId = res.data?.recentSubmission2?.id;
    } else if (res.data?.recentSubmission3?.jobCode === candidate.jobCode) {
      interactionId = res.data?.recentSubmission3?.id;
    } else if (res.data?.recentSubmission4?.jobCode === candidate.jobCode) {
      interactionId = res.data?.recentSubmission4?.id;
    } else if (res.data?.recentSubmission5?.jobCode === candidate.jobCode) {
      interactionId = res.data?.recentSubmission5?.id;
    }
    return interactionId;
  };

  useEffect(() => {
    if (view === 'submission') {
      isEmpty(profileInfo) && setIndex(0);
    }
  }, [profileInfo, setIndex, tabIndex, view]);

  useEffect(() => {
    showJobScreen != null && setShowJobSelectionScreen(showJobScreen);
  }, [showJobScreen]);

  useEffect(() => {
    if (aggregatedData && aggregatedData?.jobInteraction && isStateInitialized) {
      setActivity(aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []);
      setTotalConversation((aggregatedData?.mappings + aggregatedData?.submissions).toString());
      setAggregation(aggregatedData);
    }
  }, [aggregatedData, isStateInitialized]);

  useEffect(() => {
    if (!isStateInitialized) {
      if (aggregatedData) {
        setActivity(aggregatedData?.recentInteractions ? aggregatedData.recentInteractions : []);
        setTotalConversation((aggregatedData?.mappings + aggregatedData?.submissions).toString());
        setAggregation(aggregatedData);
      }
      setIsStateInitialized(true);
    }
  }, [aggregatedData, isStateInitialized, searchQuery]);

  const onActivityUpdate = async (interactionId) => {
    if (interactionId) {
      const res = await getJobCandidateInteraction(interactionId);
      syncPostgreAndESCandidate(res.data, state.candidate);
      updateActivityConversation(res.data);
    }
  };

  const onSubmissionStatusUpdate = async (interactionId) => {
    if (interactionId) {
      const res = await getJobCandidateInteraction(interactionId);
      setUpdateStatusCandidateInteraction(res.data);
      setOpenStatusModal(true);
    }
  };

  const loadMoreActivity = async () => {
    if (aggregatedData?.candidateEntity.candidateId) {
      const res = await getAllInteractions(aggregatedData?.candidateEntity.candidateId);
      setActivity(res.data);
    }
  };

  const loadHistory = async (id: string) => {
    setTaskFilterJobId(null);
    const res = await getJobCandidateInteraction(id);
    setTaskFilterJobId(res.data.jobCode);
    setActivityHistory(res.data);
  };

  const copyStringToClipboard = (data: string) => {
    function handler(event) {
      event.clipboardData.setData('text/plain', data);
      event.preventDefault();
      document.removeEventListener('copy', handler, true);
    }
    document.addEventListener('copy', handler, true);
    document.execCommand('copy');
  };

  const handleProfileAction = async (action: string, value: string | null) => {
    if (action === 'copy' && value) {
      copyStringToClipboard(value);
      setNotification(success('Copied Successfully !!'));
    } else if (action === 'mail' && value) {
      setSelectedEmail(value);
      if (selectedJob) updateAction('email');
      else {
        setActionAfterJob(action);
        setNotification(warning('Please select job first'));
        jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
      }
    } else if (action === 'sms' && value) {
      setSelectedPhoneNumber(value);
      if (selectedJob) updateAction('sms');
      else {
        setActionAfterJob(action);
        setNotification(warning('Please select job first'));
        jobScreen.setShowJobScreen && jobScreen.setShowJobScreen({ showJobScreen: true });
      }
    } else if (action === 'mailPrimary') {
      if (aggregatedData?.candidateEntity.candidateId) {
        await addingContactLinkInCandidateMaster({
          id: aggregatedData?.candidateEntity.candidateId,
          newEmail: value,
          updatePrimaryEmail: true,
        });
        setNotification(success('Primary email updated'));
        refreshModal(aggregatedData?.candidateEntity.candidateId);
      }
    } else {
      setOpenUpdateModal(true);
      setActionAndValue({
        action: action,
        value: value,
      });
    }
  };

  const cvAction = (action: string, url: string | null, fileName: string | null) => {
    fileName && cvViewAndDownload(action, url, fileName, cvViewSourcePage);
    if (action === 'view') {
      setResumeURL(url);
      setOpenCvView(true);
    }
  };

  const deleteCv = async (id: string, name: string) => {
    if (!isEmpty(id) && name) {
      try {
        await deleteResume(id ?? '', name);
        setNotification({
          message: 'CV deleted Successfully',
          type: 'success',
        });
        setUpdateResume(true);
        setIndex(view === 'submission' ? 0 : 1);
      } catch (e) {
        console.log(e);
      }
    } else {
      setNotification({
        message: 'please select mapping',
        type: 'warning',
      });
    }
  };

  const getFieldSettings = async (isMapping: boolean) => {
    if (isMapping) {
      const req = await fetchCandiateFieldMapping();
      if (!isEmpty(req.data)) {
        return req.data;
      } else {
        const resetDefaultSettingsDetails = await candiateDefaultFieldMapping();
        return resetDefaultSettingsDetails.data;
      }
    }
    const req = await getSubmissionSettings();
    return req.data;
  };

  return (
    <>
      <Helmet>
        <title>{aggregatedData?.candidateEntity?.name ?? ''}</title>
      </Helmet>
      {isStateInitialized && (
        <ConversationWidgetLayout
          state={state}
          selectedJob={selectedJob}
          aggregation={aggregatedData}
          update={updateResume}
        >
          {!showJobSelectionScreen && view !== 'submission' && (
            <>
              <ScrollableTabs
                className="scrollable-tabs"
                scrollList={
                  eraseData
                    ? dncScrollList()
                    : mappingScrollList({
                        activity: totalConversation
                          ? Number(totalConversation)
                          : aggregatedData?.recentInteractions
                          ? aggregatedData?.recentInteractions.length
                          : 0,
                        task: 0,
                      })
                }
                defaultValue={tabIndex}
                getTabIndex={setIndex}
              />
              {tabIndex === 0 && !eraseData && (
                <ActivityTab
                  isInDnc={isDnc}
                  totalConversation={totalConversation}
                  conversations={activity}
                  history={activityHistory}
                  getFieldSettings={getFieldSettings}
                  onSubmissionStatusUpdate={onSubmissionStatusUpdate}
                  onActivityUpdate={onActivityUpdate}
                  onLoadMore={loadMoreActivity}
                  onClickOnJobCode={(jobCode: string) => redirectToJob(jobCode ?? '')}
                  loadHistory={loadHistory}
                  selectedJob={selectedJob}
                  taskLisiting={
                    taskFilterJobId ? (
                      <InteractionTaskList state={state} hideEmptyScreen={true} />
                    ) : (
                      <></>
                    )
                  }
                  showJobRealtedConverationOnTop={aggregatedData?.jobInteraction?.id}
                  userEmail={loggedInUser?.email}
                  jobAdminTeam={isJobAdminTeamMember}
                />
              )}
              {tabIndex === 1 && (
                <>
                  {!isDnc && profileInfo ? (
                    <CandidateProfileTab
                      ss="large"
                      candidate={profileInfo}
                      aggregation={aggregation}
                      handleProfileAction={handleProfileAction}
                      cvAction={cvAction}
                      deleteCv={deleteCv}
                      otherDocuments={otherDocuments}
                      isAdmin={workspaceContext.owns}
                      getFieldSettings={getFieldSettings}
                      view={view}
                    />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}

              {tabIndex === 2 && !eraseData && (
                <Box>
                  {!isDnc ? (
                    <InteractionTaskList state={state} />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </Box>
              )}
              {tabIndex === 3 && !eraseData && (
                <>
                  <Box textAlign="center" mt={2}>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 0 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(0)}
                    >
                      Call
                    </SwitchButton>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 1 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(1)}
                    >
                      SMS
                    </SwitchButton>
                    <SwitchButton
                      noWidth
                      variant="outlined"
                      bgColor={planIndex === 2 ? WsColors.TheBlues100 : WsColors.White}
                      onClick={() => setPlanIndex(2)}
                    >
                      Email
                    </SwitchButton>
                  </Box>
                  {planIndex == 0 && (
                    <CandidateCallLogs candidate={candidate} aggregation={aggregation} />
                  )}
                  {planIndex == 1 && (
                    <CandidateSmsLogs candidate={profileInfo} aggregation={aggregation} />
                  )}
                  {planIndex == 2 && (
                    <>
                      {!isDnc ? (
                        <EmailLogs candidateEmail={state.candidate.email} />
                      ) : (
                        <Grid item xs={9} m={'Auto'} px={5}>
                          <CustomDNCMessage
                            candidate={state.candidate}
                            onClick={() => setTabIndex(4)}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              {tabIndex === 4 && !eraseData && (
                <DataPrivacy
                  email={state.candidate.email}
                  candidateName={state.candidate.name}
                  isInDnc={isDnc}
                  dncDetails={state.candidate.dncDetails}
                />
              )}
            </>
          )}
          {!showJobSelectionScreen && view === 'submission' && (
            <>
              <ScrollableTabs
                scrollList={
                  eraseData
                    ? dncScrollList()
                    : submissionScrollList(
                        activity.filter((candidate: IJobCandidateInteractionResponse) =>
                          candidate.candidateStatusTags.includes('submitted')
                        ).length ?? '0'
                      )
                }
                defaultValue={tabIndex}
                getTabIndex={(index: number) => setTabIndex(index)}
              />

              {tabIndex === 0 && (
                <>
                  {!isDnc && profileInfo && (
                    <CandidateProfileTab
                      ss="large"
                      candidate={profileInfo}
                      aggregation={aggregation}
                      handleProfileAction={handleProfileAction}
                      cvAction={cvAction}
                      deleteCv={deleteCv}
                      isAdmin={workspaceContext.owns}
                      otherDocuments={otherDocuments}
                      getFieldSettings={getFieldSettings}
                      view={view}
                    />
                  )}
                  {isDnc && (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 1 && !eraseData && (
                <>
                  {!isDnc ? (
                    <SubmissionTimeline
                      candidate={state.candidate}
                      getFieldSettings={getFieldSettings}
                    />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 2 && !eraseData && (
                <>
                  {!isDnc ? (
                    <SubmissionHistory
                      isInDnc={isDnc}
                      totalConversation={totalConversation}
                      conversations={activity}
                      history={activityHistory}
                      onSubmissionStatusUpdate={onSubmissionStatusUpdate}
                      onActivityUpdate={onActivityUpdate}
                      onLoadMore={loadMoreActivity}
                      onClickOnJobCode={(jobCode: string) => redirectToJob(jobCode ?? '')}
                      loadHistory={loadHistory}
                      selectedJob={selectedJob}
                      taskLisiting={
                        taskFilterJobId ? (
                          <InteractionTaskList state={state} hideEmptyScreen={true} />
                        ) : (
                          <></>
                        )
                      }
                      jobAdminTeam={isJobAdminTeamMember}
                    />
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 3 && !eraseData && (
                <>
                  {!isDnc ? (
                    <Grid item xs={12} display="flex" justifyContent="center" pt={12}>
                      <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY}>
                        Coming Soon
                      </WsTypography>
                    </Grid>
                  ) : (
                    <Grid item xs={9} m={'Auto'} px={5}>
                      <CustomDNCMessage
                        candidate={state.candidate}
                        onClick={() => setTabIndex(4)}
                      />
                    </Grid>
                  )}
                </>
              )}
              {tabIndex === 4 && !eraseData && (
                <DataPrivacy
                  email={state.candidate.email}
                  candidateName={state.candidate.name}
                  isInDnc={isDnc}
                  dncDetails={state.candidate.dncDetails}
                />
              )}
            </>
          )}
          {showJobSelectionScreen && (
            <JobSelection onBackButton={() => setShowJobSelectionScreen(false)} />
          )}
        </ConversationWidgetLayout>
      )}
      {openStatusModal && (
        <StageStatusContextProvider>
          <UpdateStatusModal
            candidate={updateStatusCandidateInteraction}
            openStatusModal={openStatusModal}
            onClose={closeUpdateStatusModal}
          />
        </StageStatusContextProvider>
      )}
      {openUpdateModal && actionAndValue && aggregatedData?.candidateEntity?.candidateId && (
        <UpdateModal
          id={aggregatedData?.candidateEntity.candidateId}
          open={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
          actionAndValue={actionAndValue}
          candidate={state.candidate}
        />
      )}
      {!isEmpty(profileInfo?.allCandidateResumes) && (
        <CVViewModal open={openCvView} s3url={resumeURl} handleClose={() => setOpenCvView(false)} />
      )}
    </>
  );
};

export default ConversationWidgetUI;
